import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Helmet } from 'react-helmet-async';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Shield, Award, Calendar, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

const ManufacturerWarrantiesPage = () => {
  return (
    <Layout>
      <Helmet>
        <title>Manufacturer Warranties Explained | Carolina Trophy Roofs</title>
        <meta name="description" content="Understand manufacturer warranties from top roofing brands like GAF, Owens Corning, IKO, Atlas, and CertainTeed. Learn about material coverage, terms, and how to maximize your warranty protection." />
        <meta name="keywords" content="manufacturer warranty, GAF warranty, Owens Corning warranty, roofing material warranty, shingle warranty, North Carolina" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/resources/manufacturer-warranties" />
      </Helmet>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <Link to="/resources" className="text-primary hover:underline">Resources</Link>
            <span className="mx-2">→</span>
            <span className="text-muted-foreground">Manufacturer Warranties Explained</span>
          </nav>

          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <Award className="mr-2 h-4 w-4" />
              Warranty Information
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Manufacturer Warranties Explained
            </h1>
            <p className="text-xl text-gray-600">
              Understanding material warranties from industry-leading brands
            </p>
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
              <h2 className="text-xl font-semibold text-blue-800 mb-2">Dual Warranty Protection</h2>
              <p className="text-blue-700">
                When you choose Carolina Trophy Roofs, you get <strong>two layers of protection</strong>: 
                our 10-year workmanship warranty plus comprehensive manufacturer warranties from 
                top-tier brands like GAF, Owens Corning, IKO, Atlas, and CertainTeed.
              </p>
            </div>

            <h2 className="text-2xl font-bold mb-6">Leading Brand Warranties</h2>
            
            <div className="grid gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center mb-4">
                  <img src="https://www.iko.com/favicon.ico" alt="IKO" className="w-8 h-8 mr-3" />
                  <h3 className="text-xl font-semibold">IKO Warranties</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">Shield Pro Plus™ Limited Warranty</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>Lifetime</strong> limited warranty</li>
                      <li>• Wind resistance up to 130 mph</li>
                      <li>• Algae protection guarantee</li>
                      <li>• Non-prorated for first 15 years</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Cambridge™ Series</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>25-year</strong> limited warranty</li>
                      <li>• Wind warranty up to 110 mph</li>
                      <li>• Algae resistance protection</li>
                      <li>• Transferable warranty</li>
                    </ul>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Source: <a href="https://www.iko.com/na/warranty/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">IKO Warranty Information</a>
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center mb-4">
                  <img src="https://www.atlasroofing.com/favicon.ico" alt="Atlas" className="w-8 h-8 mr-3" />
                  <h3 className="text-xl font-semibold">Atlas Warranties</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">StormMaster® Shake & Slate</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>Limited lifetime</strong> warranty</li>
                      <li>• Class 4 impact rating</li>
                      <li>• 130 mph wind warranty</li>
                      <li>• Scotchgard™ Algae Guard</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Pinnacle® Pristine</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>Limited lifetime</strong> warranty</li>
                      <li>• 130 mph wind resistance</li>
                      <li>• Algae resistance guarantee</li>
                      <li>• Non-prorated coverage</li>
                    </ul>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Source: <a href="https://www.atlasroofing.com/warranty/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Atlas Roofing Warranty Center</a>
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center mb-4">
                  <img src="https://www.owenscorning.com/favicon.ico" alt="Owens Corning" className="w-8 h-8 mr-3" />
                  <h3 className="text-xl font-semibold">Owens Corning Warranties</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">Platinum Protection Period</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>50-year</strong> limited warranty</li>
                      <li>• TruDefinition® Duration® Series</li>
                      <li>• 130 mph wind resistance</li>
                      <li>• Algae resistance protection</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">System Warranty</h4>
                    <ul className="text-sm space-y-1">
                      <li>• Complete roofing system coverage</li>
                      <li>• Enhanced wind coverage</li>
                      <li>• Labor cost coverage available</li>
                      <li>• Non-prorated protection</li>
                    </ul>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Source: <a href="https://www.owenscorning.com/en-us/roofing/warranties" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Owens Corning Warranty Center</a>
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center mb-4">
                  <img src="https://www.certainteed.com/favicon.ico" alt="CertainTeed" className="w-8 h-8 mr-3" />
                  <h3 className="text-xl font-semibold">CertainTeed Warranties</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">Integrity Roof System™</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>Lifetime</strong> transferable warranty</li>
                      <li>• SureStart™ protection</li>
                      <li>• 130 mph wind warranty</li>
                      <li>• StreakFighter™ algae resistance</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Presidential Shake™ TL</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>Lifetime</strong> limited warranty</li>
                      <li>• Class 4 impact rating</li>
                      <li>• 110 mph wind resistance</li>
                      <li>• Advanced algae protection</li>
                    </ul>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Source: <a href="https://www.certainteed.com/roofing/warranty/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CertainTeed Warranty Information</a>
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center mb-4">
                  <img src="https://www.gaf.com/favicon.ico" alt="GAF" className="w-8 h-8 mr-3" />
                  <h3 className="text-xl font-semibold">GAF Warranties</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">System Plus™ Limited Warranty</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>50-year</strong> non-prorated coverage</li>
                      <li>• Covers shingles, ridge cap, leak barrier</li>
                      <li>• Wind warranty up to 130 mph</li>
                      <li>• Algae protection guarantee</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Golden Pledge™ Limited Warranty</h4>
                    <ul className="text-sm space-y-1">
                      <li>• <strong>25-year</strong> workmanship coverage</li>
                      <li>• <strong>50-year</strong> material coverage</li>
                      <li>• Covers labor and materials</li>
                      <li>• Transferable to new owners</li>
                    </ul>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Source: <a href="https://www.gaf.com/en-us/for-homeowners/warranties" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">GAF Warranty Information</a>
                </p>
              </div>
            </div>

            
            <h2 className="text-2xl font-bold mb-4">Understanding Warranty Terms</h2>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">Key Warranty Concepts</h3>
              <div className="grid md:grid-cols-2 gap-4 text-yellow-700">
                <div>
                  <h4 className="font-medium mb-2">Prorated vs. Non-Prorated</h4>
                  <p className="text-sm">
                    <strong>Non-prorated:</strong> Full coverage value maintained throughout warranty period. 
                    <strong>Prorated:</strong> Coverage value decreases over time.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Limited vs. Full Warranty</h4>
                  <p className="text-sm">
                    <strong>Limited:</strong> Covers specific defects and conditions. 
                    <strong>Full:</strong> More comprehensive coverage with fewer exclusions.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Warranty Registration Requirements</h2>
            <p className="mb-4">
              According to the <a href="https://www.nrca.net" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">National Roofing Contractors Association</a>, 
              proper warranty registration is crucial for maintaining coverage. Most manufacturers require registration within 60-90 days of installation.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-green-50 p-6 rounded-lg">
                <Calendar className="h-8 w-8 text-green-500 mb-4" />
                <h3 className="text-lg font-semibold mb-3">Registration Timeline</h3>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>IKO:</strong> 60 days from installation</li>
                  <li>• <strong>Atlas:</strong> 60 days from installation</li>
                  <li>• <strong>Owens Corning:</strong> 90 days from installation</li>
                  <li>• <strong>CertainTeed:</strong> 60 days from installation</li>
                  <li>• <strong>GAF:</strong> 60 days from installation</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <Shield className="h-8 w-8 text-blue-500 mb-4" />
                <h3 className="text-lg font-semibold mb-3">Required Information</h3>
                <ul className="space-y-2 text-sm">
                  <li>• Property owner information</li>
                  <li>• Installation date and location</li>
                  <li>• Product specifications and quantities</li>
                  <li>• Certified contractor information</li>
                  <li>• Proof of purchase documentation</li>
                </ul>
              </div>
            </div>

            
            <h2 className="text-2xl font-bold mb-4">Maximizing Your Warranty Protection</h2>
            <div className="bg-gray-50 p-6 rounded-lg mb-8">
              <h3 className="text-lg font-semibold mb-4">Best Practices for Warranty Maintenance</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">Regular Maintenance</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Annual professional inspections</li>
                    <li>• Keep gutters clean and functional</li>
                    <li>• Trim overhanging tree branches</li>
                    <li>• Address minor issues promptly</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Documentation</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Keep all warranty certificates</li>
                    <li>• Maintain installation photos</li>
                    <li>• Document any maintenance performed</li>
                    <li>• Save contractor certifications</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4">Common Warranty Claims</h2>
            <p className="mb-4">
              Research from the <a href="https://www.rci-online.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Roof Consultants Institute</a> 
              shows that most warranty claims fall into these categories:
            </p>
            
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="text-center p-4 bg-white rounded-lg border">
                <div className="text-2xl font-bold text-primary mb-2">45%</div>
                <div className="text-sm">Manufacturing Defects</div>
              </div>
              <div className="text-center p-4 bg-white rounded-lg border">
                <div className="text-2xl font-bold text-primary mb-2">30%</div>
                <div className="text-sm">Premature Granule Loss</div>
              </div>
              <div className="text-center p-4 bg-white rounded-lg border">
                <div className="text-2xl font-bold text-primary mb-2">25%</div>
                <div className="text-sm">Wind Damage Claims</div>
              </div>
            </div>

            <div className="text-center mt-12">
              <h2 className="text-2xl font-bold mb-4">Need Help with Warranty Registration?</h2>
              <p className="text-lg text-muted-foreground mb-6">
                We handle all warranty registrations for our customers and provide ongoing support.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg">
                  <Link to="/contact">Get Warranty Support</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/resources">
                    <FileText className="mr-2 h-5 w-5" />
                    More Resources
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ManufacturerWarrantiesPage;
